import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { ROLE_LABELS, ROLE_COLORS } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Factory, LogOut, Wifi, WifiOff, HelpCircle } from "lucide-react";
import HelpDialog from "@/components/help/HelpDialog";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Navbar({ realtime }) {
  const { user, logout } = useAuth();
  const { connected, latency } = realtime;
  const [helpOpen, setHelpOpen] = useState(false);
  const t = useT();

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-slate-200 shadow-sm">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center text-white">
            <Factory className="h-5 w-5" />
          </div>
          <div className="hidden sm:block">
            <p className="font-display text-base font-bold text-slate-900 leading-none">H. K. INDUSTRIES</p>
            <p className="text-[10px] text-slate-400 tracking-widest uppercase">Industrial Operations</p>
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-5">
          <div
            className="hidden sm:flex items-center gap-2 rounded-full px-3 py-1.5 border"
            style={{ borderColor: connected ? "#6EE7B7" : "#FCA5A5", background: connected ? "#ECFDF5" : "#FEF2F2" }}
            data-testid="nav-websocket-sync-indicator"
          >
            {connected ? (
              <>
                <span className="h-2 w-2 rounded-full bg-emerald-500 pulse-dot" />
                <Wifi className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-xs font-medium text-emerald-700">{t("nav.live")}</span>
                {latency != null && <span className="text-[10px] font-mono-data text-emerald-600">{latency}ms</span>}
              </>
            ) : (
              <>
                <span className="h-2 w-2 rounded-full bg-red-500" />
                <WifiOff className="h-3.5 w-3.5 text-red-600" />
                <span className="text-xs font-medium text-red-700">{t("nav.offline")}</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2" data-testid="nav-role-badge">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-slate-800 leading-none">{user.name}</p>
              <p className="text-[11px] text-slate-400 font-mono-data">@{user.username}</p>
            </div>
            <span className={`text-[10px] font-mono-data uppercase tracking-wide px-2 py-1 rounded ${ROLE_COLORS[user.role]}`}>
              {ROLE_LABELS[user.role]}
            </span>
          </div>

          <LanguageSwitcher />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setHelpOpen(true)}
            data-testid="help-button"
            title={t("nav.help")}
            className="text-slate-500 hover:text-blue-600 gap-1.5"
          >
            <HelpCircle className="h-4 w-4" />
            <span className="hidden md:inline text-xs font-medium">{t("nav.help")}</span>
          </Button>

          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button" title={t("nav.logout")} className="text-slate-500 hover:text-red-600">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </header>
  );
}
